import {
  ColumnDef,
  functionalUpdate,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EventAccordionItemBodyComponentType } from 'src/components/Accordions';
import { useSingleTableShortcutSelection } from 'src/components/Events/EventPage/hooks/useSingleTableShortcutSelection';
import { useAppContext } from 'src/contexts/AppContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { useEnableSelectionByDefault } from 'src/hooks/useEnableSelectionByDefault';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { NoData } from 'src/tables/Table';
import { Table } from 'src/tables/TableV2/Table';
import { createNumericColorGradientThresholds } from 'src/utils/cellUtils';
import {
  defaultSalesColumnsConfig,
  filterColumnsByFeatures,
  getSaleColumnConfigById,
} from 'src/utils/columns/columnUtils';
import { SALE_MAIN_COLUMNS_V2 } from 'src/utils/columns/sales/salesColumnUtils.constants';
import {
  evaluateFormula,
  filterCustomColumnsForSale,
} from 'src/utils/columns/sales/salesCustomColumnUtils';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { generateShimmeringRows } from 'src/utils/dataTableUtils';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import { createAggregatedSale } from 'src/utils/saleUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Permission,
  Sale,
  UserSetting,
} from 'src/WebApiController';

import { SalesTableColumnId } from '../../utils/columns/sales/salesColumnUtils.types';
import { useSeatingColumnSorting } from '../common/hooks/useSeatingColumnSorting';
import { createCustomColumn } from '../TableV2/factories/customColumnFactory';
import { createTagColumn } from '../TableV2/factories/tagColumnFactory';
import { SALE_TABLE_COLUMNS_CONFIG_V2 } from './configs/SaleTableColumnsConfigV2';
import { SaleWithEvent } from './SalesTable.type';

export type SalesTableV2Props = EventAccordionItemBodyComponentType<Sale> & {
  alwaysShowCheckbox?: boolean;
  showSelectAllOnHeader?: boolean;
};

const PAGE_SIZE = 10;

export const SalesTableV2 = ({
  event,
  entities: sales,
  entitiesSummary: salesSummary,
  entityCount: salesCount,
  getDataFail: failedToRetrieveData,
  onMount,
  onUnmount,
  initState,
  disablePagination,
  useVirtuoso,
  isSideTable = false,
  onItemSelectionsChanged,
  alwaysShowCheckbox,
  showSelectAllOnHeader,
}: SalesTableV2Props) => {
  const enableSelectionByDefault = useEnableSelectionByDefault();
  alwaysShowCheckbox = alwaysShowCheckbox || enableSelectionByDefault;

  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Sale
  );

  const showMainColumnsOnly = false;
  const isItemsLoading = salesCount > 0 && (sales == null || sales.length == 0);
  const data = useMemo(() => {
    const result = sales?.map((sale) => ({ sale, event }));

    return result?.length
      ? result
      : result == null || isItemsLoading
        ? (generateShimmeringRows(salesCount) as null[])
        : [];
  }, [sales, isItemsLoading, salesCount, event]);

  const hasViewProceeds = useUserHasAnyOfPermissions(
    Permission.Sales_ViewProceeds,
    Permission.Sales_ViewRecentProceeds
  );

  const { appContext, loginContext, mainRoute } = useAppContext();
  const inFullPageEventView = isInFullPageEventView(mainRoute);

  const salesText = useContent(ContentId.Sales);

  const {
    lastSelectedRowRefIndex,
    allowShiftClickSelection,
    isShiftKeyPressed,
    isCtrlOrCmdKeyPressed,
    updateSelectionForRowAndSubRows,
  } = useSingleTableShortcutSelection<SaleWithEvent>({ isSideTable });

  const {
    setGroupItems,
    getGroupToggleState,
    getSelection,
    selectionMode,
    updateGroupFilteredIds,
  } = useMultiSelectionContext();

  const saleSelection = getSelection(event.virtId);

  // Enable passing in table state as parameters -- we can remount with the last state the user was on
  const [sorting, setSorting] = useState<SortingState>(
    initState?.sorting || []
  );
  const [pagination, setPagination] = useState<PaginationState | undefined>(
    disablePagination
      ? undefined
      : {
          pageIndex: initState?.pagination?.pageIndex || 0,
          pageSize: PAGE_SIZE,
        }
  );

  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  const seatingColumnSorting = useSeatingColumnSorting<SaleWithEvent>(
    SalesTableColumnId.Seating,
    SectionType.Sales
  );

  const groupId = event.virtId;
  const rowSelection = useMemo(() => {
    return data
      ?.flatMap((d) => [d?.sale])
      ?.reduce<Record<string, boolean>>((result, sale) => {
        if (sale?.id) {
          const isGroupSelectedWithoutClientSideFilter =
            getGroupToggleState(groupId).isGroupSelected &&
            !saleSelection.items.itemIds.length;

          result[sale.id] = isGroupSelectedWithoutClientSideFilter
            ? true
            : ((saleSelection?.items.itemIds ?? [])?.includes(
                String(sale?.id)
              ) ?? false);
        }
        return result;
      }, {});
  }, [data, groupId, getGroupToggleState, saleSelection.items.itemIds]);

  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;

      const newSelectedIds = Object.keys(newSelections).filter(
        (r) => newSelections[r] === true
      );

      setGroupItems(event.virtId, newSelections);
      onItemSelectionsChanged?.(
        newSelectedIds
          .filter((id) => newSelections[id])
          .map((id) => parseInt(id))
      );
    },
    [event.virtId, onItemSelectionsChanged, rowSelection, setGroupItems]
  );

  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      saleSelection.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      saleSelection.isSingleGroupMultiSelect,
      selectionMode?.mode,
    ]
  );

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ sorting });
  }, [sorting, onUnmount]);

  // Clear selections when selection mode changes
  useEffect(() => {
    onItemSelectionsChanged?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode?.mode]);

  const {
    value: storedInventoryColumnOrderSetting = defaultSalesColumnsConfig,
  } = useServerUserSetting<SalesTableColumnId[]>({
    id: isSideTable
      ? UserSetting.SalesSideTableColumnOrder
      : UserSetting.SaleColumnOrder,
  });
  const { value: storedSaleColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: isSideTable
      ? UserSetting.SalesSideTableColumnsEnabled
      : UserSetting.SaleColumnsEnabled,
  });

  const { value: customSaleColumns = [] } = useServerUserSetting<
    CustomSalesColumn[]
  >({
    id: UserSetting.SaleCustomColumns,
  });
  const { tagsMetadata, tagsMetadataNumeric } = useTagsForEntityType(
    ActionOutboxEntityType.Sale,
    true
  );

  const customSaleColumnsFiltered = filterCustomColumnsForSale(
    customSaleColumns,
    tagsMetadataNumeric
  );

  const displayedColumnsConfig = useMemo(() => {
    if (showMainColumnsOnly) {
      return SALE_MAIN_COLUMNS_V2.reduce<ColumnDef<SaleWithEvent | null>[]>(
        (acc, columnId) => {
          const columnConfig = Object.values(SALE_TABLE_COLUMNS_CONFIG_V2).find(
            (column) => column.id === columnId
          );
          if (columnConfig) {
            acc.push(columnConfig);
          }
          return acc;
        },
        []
      );
    }

    const saleColumnOrder = storedInventoryColumnOrderSetting.filter(
      // If storedSaleColumnsEnabledSetting has never been set, default to true
      (c) => storedSaleColumnsEnabledSetting?.includes(c) ?? true
    );

    const salesTableColumnsConfig = Object.values(SALE_TABLE_COLUMNS_CONFIG_V2);

    const columns = new Set([
      // Add unconfigurable columns first
      ...salesTableColumnsConfig
        .filter((column) => {
          const columnDef = getSaleColumnConfigById(
            column.id as SalesTableColumnId
          ).personalization;
          const hasFeatureForColumn =
            columnDef.requiredFeatures.length === 0 ||
            hasFeatures(
              loginContext?.user,
              appContext?.features,
              columnDef.requiredFeatures
            );

          const isConfigurable = columnDef.isConfigurable;

          return !isConfigurable && hasFeatureForColumn;
        })
        .map((item) => item.id as SalesTableColumnId),
      // Add user defined columns next
      ...filterColumnsByFeatures(
        saleColumnOrder,
        SectionType.Sales,
        customSaleColumnsFiltered,
        loginContext?.user,
        appContext?.features,
        tagsMetadata ?? []
      ),
    ]);

    if (showCheckbox) {
      if (!alwaysShowCheckbox) {
        columns.delete(SalesTableColumnId.Actions);
      }
    } else {
      columns.delete(SalesTableColumnId.Checkbox);
    }

    return Array.from(columns).reduce<ColumnDef<SaleWithEvent | null>[]>(
      (acc, columnId) => {
        if (tagsMetadata?.find((t) => t.key === columnId)) {
          acc.push(
            createTagColumn<SaleWithEvent | null>({
              columnId,
              tagsAccessor: (data) => data?.sale?.tags,
              currencyAccessor: (data) => data?.sale?.currency,
              sectionType: SectionType.Sales,
            })
          );
        } else if (customSaleColumnsFiltered.find((c) => c.id === columnId)) {
          acc.push(
            createCustomColumn<SaleWithEvent | null>({
              columnId,
              formula: customSaleColumnsFiltered.find((c) => c.id === columnId)
                ?.formula,
              formulaAccessor: (data, formula) =>
                formula && data?.sale
                  ? (evaluateFormula(formula, data.sale) ?? null)
                  : null,
              currencyAccessor: (data) => data?.sale?.currency,
              customColumnsAccessor: () => customSaleColumnsFiltered,
            })
          );
        } else {
          const columnConfig = salesTableColumnsConfig.find(
            (column) => column.id === columnId
          );
          if (columnConfig) acc.push(columnConfig);
        }

        return acc;
      },
      []
    );
  }, [
    alwaysShowCheckbox,
    appContext?.features,
    customSaleColumnsFiltered,
    loginContext?.user,
    showCheckbox,
    showMainColumnsOnly,
    storedInventoryColumnOrderSetting,
    storedSaleColumnsEnabledSetting,
    tagsMetadata,
  ]);

  const [visibleRowIds, setVisibleRowIds] = useState<number[]>();

  const salesSummaryFiltered = useMemo(() => {
    if (!salesSummary || !hasTableTotalsFeature) {
      return null;
    }

    if (visibleRowIds) {
      // If there's client-side filtering, the best we can do is aggregate the sales and skip those with different currency
      return createAggregatedSale(
        data
          ?.filter((d) => d && visibleRowIds.includes(d.sale.id))
          .map((d) => d!.sale)
      );
    }
    return salesSummary;
  }, [visibleRowIds, data, salesSummary, hasTableTotalsFeature]);

  // Calculate thresholds for P&L and Margin columns
  const numericColorThresholds = useMemo(() => {
    const pandlValues = data
      .map((d) => d?.sale?.pandL?.amt)
      .filter((val): val is number => val != null);

    const marginValues = data
      .map((d) => d?.sale?.margin)
      .filter((val): val is number => val != null);

    return {
      [SalesTableColumnId.PandL]:
        createNumericColorGradientThresholds(pandlValues),
      [SalesTableColumnId.Margin]:
        createNumericColorGradientThresholds(marginValues),
    };
  }, [data]);

  const onPaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      if (!pagination) return;

      const newPagination = functionalUpdate(updater, pagination);
      setPagination(newPagination);
    },
    [pagination]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = functionalUpdate(updater, sorting);
      setSorting(newSorting);
    },
    [setSorting, sorting]
  );

  const onRowShiftSelect = useCallback(
    (
      tableRows: Row<SaleWithEvent | null>[] | undefined,
      row: Row<SaleWithEvent | null>,
      index: number
    ) => {
      if (!tableRows || !allowShiftClickSelection || showCheckbox) {
        return;
      }
      const rowId = row.id.toString();
      const newIsSelected = !rowSelection[rowId];
      if (isShiftKeyPressed && lastSelectedRowRefIndex.current !== null) {
        const start = Math.min(lastSelectedRowRefIndex.current, index);
        const end = Math.max(lastSelectedRowRefIndex.current, index);

        const newRowSelection = { ...rowSelection };
        for (let i = start; i <= end; i++) {
          const row = tableRows[i];
          if (row) {
            updateSelectionForRowAndSubRows({
              row,
              rowId: row.id.toString(),
              newIsSelected,
              newRowSelection,
            });
          }
        }

        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      } else if (isCtrlOrCmdKeyPressed) {
        const newRowSelection = { ...rowSelection };
        newRowSelection[rowId] = newIsSelected;
        updateSelectionForRowAndSubRows({
          row,
          rowId,
          newIsSelected, // Select the clicked row
          newRowSelection,
        });
        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      } else {
        // No modifier key pressed, reset selection
        const newRowSelection = {};

        updateSelectionForRowAndSubRows({
          row,
          rowId,
          newIsSelected,
          newRowSelection,
        });
        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      }
    },
    [
      allowShiftClickSelection,
      isCtrlOrCmdKeyPressed,
      isShiftKeyPressed,
      lastSelectedRowRefIndex,
      onRowSelectionChange,
      rowSelection,
      showCheckbox,
      updateSelectionForRowAndSubRows,
    ]
  );

  // Memoize state object to prevent VirtuosoTable rerenders
  const tableState = useMemo(
    () => ({
      pagination,
      sorting,
      rowSelection,
      columnVisibility: {
        checkbox: showCheckbox,
      },
    }),
    [pagination, sorting, rowSelection, showCheckbox]
  );

  // Memoize meta object to prevent VirtuosoTable rerenders
  const tableMeta = useMemo(
    () => ({
      sectionType: isSideTable ? SectionType.SalesSideTable : undefined,
      showSelectAllCheckbox:
        selectionMode?.mode === MultiSelectScope.SingleGroup &&
        showSelectAllOnHeader,
      summaryData:
        data?.[0] && salesSummaryFiltered
          ? {
              event: data[0].event,
              sale: salesSummaryFiltered,
            }
          : undefined,
      numericColorThresholds,
    }),
    [
      isSideTable,
      selectionMode?.mode,
      showSelectAllOnHeader,
      data,
      salesSummaryFiltered,
      numericColorThresholds,
    ]
  );

  return failedToRetrieveData ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : data?.length ? (
    <ColumnResizingContextProvider userSettingId={UserSetting.SaleColumnWidths}>
      <Table<SaleWithEvent>
        useVirtuoso={useVirtuoso}
        actionColId={SalesTableColumnId.Actions}
        data={data as SaleWithEvent[]}
        columns={
          displayedColumnsConfig.filter((c) =>
            c.id === 'proceeds' ? hasViewProceeds : true
          ) as ColumnDef<SaleWithEvent>[]
        }
        pagination={
          disablePagination
            ? undefined
            : { pageSize: pagination?.pageSize ?? PAGE_SIZE, variant: 'input' }
        }
        getRowId={(data, index) => String(data?.sale?.id ?? index)}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        sortingFns={{ ...seatingColumnSorting }}
        onRowSelectionChange={onRowSelectionChange}
        onRowShiftSelect={onRowShiftSelect}
        alwaysShowCheckbox={alwaysShowCheckbox}
        getGroupId={(data) => data?.event?.virtId}
        getItemId={(data) => String(data?.sale?.id)}
        enableRowSelection={showCheckbox || inFullPageEventView}
        enableMultiRowSelection={showCheckbox}
        enableSubRowSelection={showCheckbox}
        state={tableState}
        meta={tableMeta}
      />
    </ColumnResizingContextProvider>
  ) : (
    // We should never see this - because the events are filtered to only those that has sales
    // So this is to prevent a crash - but this should be looked at
    <NoData>
      <FormatContent id={FormatContentId.NoDataAvailable} params={salesText} />
    </NoData>
  );
};
